import { useEffect, useMemo, type RefObject } from "react";
import { usePaneSearch } from "@/payroll/editor/use-pane-search";

/**
 * Find a value in the sheet on screen.
 *
 * A cell is the unit, the way a row is on the roster and a passage is in the
 * written rules. Both sides go into the haystack: a value only today's payroll
 * has is still a thing to look for, and it is the side that will not be there
 * tomorrow.
 *
 * A match selects the cell rather than painting a highlight of its own, so the
 * ring and the formula bar the reader already knows do the showing.
 */
export const useComparisonSearch = ({
  sides,
  pane,
  onSelect,
}: {
  sides: { current: Map<string, string>; proposed: Map<string, string> };
  /** The pane's root, so the find shortcut only answers while it is on screen. */
  pane: RefObject<HTMLElement | null>;
  onSelect: (coord: string) => void;
}) => {
  const search = usePaneSearch(
    useMemo(() => {
      const seen = new Map<string, string>();
      const add = (from: Map<string, string>) =>
        from.forEach((value, coord) =>
          seen.set(coord, `${seen.get(coord) ?? ""} ${value}`),
        );
      add(sides.current);
      add(sides.proposed);
      return Array.from(seen, ([id, text]) => ({ id, text }));
    }, [sides]),
    pane,
  );
  const { activeId } = search;
  useEffect(() => {
    if (!activeId) return;
    onSelect(activeId);
    pane.current
      ?.querySelector(`td[data-coord="${CSS.escape(activeId)}"]`)
      ?.scrollIntoView({ block: "center", inline: "center" });
  }, [activeId, onSelect, pane, sides]);
  return search;
};
